import { systemBanner } from '@components/SystemAlerts/SystemBanner';
import { useIndexedDBStore } from '@hooks/useIndexedDB';
import { TABLE_PREFERENCES_IDB_STORE } from '@hooks/useIndexedDB/config';
import { t } from '@lingui/core/macro';
import { useKeycloak } from '@react-keycloak/web';
import { SKIP_AUTH } from '@utils/constants';
import { reportCustomError } from '@utils/reportCustomError';
import { win } from '@utils/win';
import { whoAmIV2Document } from '@views/Common/Permissions';
import { useCallback, useMemo } from 'react';
import { authLogoutRedirectUri } from '../../../config';

interface Ret {
  logout: () => void;
  keycloak: Keycloak.KeycloakInstance;
  initialized: boolean;
}

export const useLogin = (): Ret => {
  const kc = useKeycloak();
  const { keycloak, initialized } = kc;
  const { deleteAll } = useIndexedDBStore(TABLE_PREFERENCES_IDB_STORE);

  const logout = useCallback(async (): Promise<void> => {
    if (!SKIP_AUTH) {
      win.localStorage.removeItem(`swr-${whoAmIV2Document}`);
      win.localStorage.removeItem(`swr-err@${whoAmIV2Document}`);

      try {
        await deleteAll();
      } catch (err) {
        reportCustomError(
          `Error deleting table preferences from indexDB | ${err}`,
        );
      }

      keycloak?.logout({
        redirectUri: authLogoutRedirectUri,
      }) ??
        systemBanner({
          title: t`Error`,
          content: t`There was an error logging out.`,
          type: 'error',
        });
    } else {
      win.location.href = authLogoutRedirectUri;
    }
  }, [deleteAll, keycloak]);

  return useMemo(
    () => ({
      logout,
      keycloak,
      initialized,
    }),
    [initialized, keycloak, logout],
  );
};
